// src/layouts/CustomLayout.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout, LayoutProps, useLogout, useNotify, useTranslate, useGetIdentity } from 'react-admin';
import { useTheme, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { CustomMenu } from './CustomMenu';
import { CustomAppBar } from './CustomAppBar';

// Componentes vacíos para Developer (solo tiene acceso a HMI Simulator)
const EmptyMenu = () => null;
const EmptySidebar = () => <div style={{ display: 'none' }} />;
import { TokenExpiryDialog } from '../components/dialogs/TokenExpiryDialog';
import { decodeJWT } from '../utils/auth';
import keycloak, { storeKeycloakSession } from '../auth/keycloak';

// Session management constants
const WARNING_THRESHOLD = 300;       // Show dialog when < 5 min remain AND user is inactive
const CHECK_INTERVAL = 10000;        // Poll token expiry every 10s
const COUNTDOWN_INTERVAL = 1000;     // Countdown tick every 1s
const INACTIVITY_THRESHOLD = 120;    // 2 min without activity → considered inactive
const REFRESH_COOLDOWN = 60000;      // Don't attempt silent refresh more than once per 60s

export const CustomLayout: React.FC<LayoutProps> = (props) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation();
    const isHMISimulator = location.pathname.includes('/hmi-simulator');
    const { data: identity } = useGetIdentity();
    const isDeveloper = identity?.role === 'Developer';
    const logout = useLogout();
    const notify = useNotify();
    const translate = useTranslate();

    // Token expiry state
    const [showExpiryDialog, setShowExpiryDialog] = useState(false);
    const [secondsRemaining, setSecondsRemaining] = useState(300);
    const [tokenExpiryTime, setTokenExpiryTime] = useState<number | null>(null);

    // Activity tracking refs
    const lastActivityRef = useRef(Date.now());
    const lastRefreshRef = useRef(0);

    // Track user activity (throttled to once per second)
    useEffect(() => {
        let lastUpdate = 0;
        const onActivity = () => {
            const now = Date.now();
            if (now - lastUpdate > 1000) {
                lastActivityRef.current = now;
                lastUpdate = now;
            }
        };
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        events.forEach(e => document.addEventListener(e, onActivity, { passive: true }));
        return () => { events.forEach(e => document.removeEventListener(e, onActivity)); };
    }, []);

    // Silent Keycloak token refresh (no UI, no notification)
    const silentRefresh = useCallback(async () => {
        const now = Date.now();
        if (now - lastRefreshRef.current < REFRESH_COOLDOWN) return; // Cooldown guard
        lastRefreshRef.current = now;
        try {
            const refreshed = await keycloak.updateToken(WARNING_THRESHOLD);
            if (refreshed) storeKeycloakSession(keycloak);
            setShowExpiryDialog(false);
        } catch {
            // Refresh token expired — can't refresh silently; let the dialog appear
        }
    }, []);

    // Extend session via dialog button (with user feedback)
    const handleExtendSession = useCallback(async () => {
        try {
            const refreshed = await keycloak.updateToken(WARNING_THRESHOLD);
            if (refreshed) storeKeycloakSession(keycloak);

            setShowExpiryDialog(false);
            setSecondsRemaining(300);

            // Read the freshly stored token to show the new expiry time
            const newToken = localStorage.getItem('token');
            if (newToken) {
                const decoded = decodeJWT(newToken);
                if (decoded?.exp) {
                    setTokenExpiryTime(decoded.exp);
                    const expirationDate = new Date(decoded.exp * 1000);
                    notify(
                        translate('common.notifications.session_extended_with_time', { time: expirationDate.toLocaleTimeString() }),
                        { type: 'success', autoHideDuration: 5000 },
                    );
                    return;
                }
            }
            notify(translate('common.notifications.session_extended_success'), { type: 'success', autoHideDuration: 5000 });
        } catch {
            // Refresh token is dead — must log in again
            notify(translate('common.notifications.session_expired'), { type: 'error' });
            setTimeout(() => logout(), 2000);
        }
    }, [logout, notify, translate]);

    // Periodic token check — decides between silent refresh and dialog
    const checkTokenExpiry = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) { setShowExpiryDialog(false); return; }

        const decoded = decodeJWT(token);
        if (!decoded?.exp) { setShowExpiryDialog(false); return; }

        const now = Math.floor(Date.now() / 1000);
        const timeLeft = decoded.exp - now;

        if (timeLeft <= 0) { logout(); return; }

        if (timeLeft <= WARNING_THRESHOLD) {
            const inactiveSeconds = (Date.now() - lastActivityRef.current) / 1000;

            if (inactiveSeconds < INACTIVITY_THRESHOLD) {
                // User is active → refresh silently, never show dialog
                silentRefresh();
            } else {
                // User is inactive → show warning dialog
                setSecondsRemaining(timeLeft);
                setTokenExpiryTime(decoded.exp);
                if (!showExpiryDialog) setShowExpiryDialog(true);
            }
        } else {
            if (showExpiryDialog) setShowExpiryDialog(false);
        }
    }, [showExpiryDialog, logout, silentRefresh]);

    // Poll token expiry
    useEffect(() => {
        checkTokenExpiry();
        const id = setInterval(checkTokenExpiry, CHECK_INTERVAL);
        return () => clearInterval(id);
    }, [checkTokenExpiry]);

    // Countdown tick while dialog is open
    useEffect(() => {
        if (!showExpiryDialog || !tokenExpiryTime) return;
        const id = setInterval(() => {
            const timeLeft = tokenExpiryTime - Math.floor(Date.now() / 1000);
            if (timeLeft <= 0) { logout(); } else { setSecondsRemaining(timeLeft); }
        }, COUNTDOWN_INTERVAL);
        return () => clearInterval(id);
    }, [showExpiryDialog, tokenExpiryTime, logout]);

    return (
        <>
            <Layout
                {...props}
                appBar={CustomAppBar}
                menu={isDeveloper ? EmptyMenu : CustomMenu}
                sidebar={isDeveloper ? EmptySidebar : undefined}
                sx={{
                    // 1. Fondo general de la página
                    '&': {
                        backgroundColor: theme.palette.background.default,
                        // react-admin's Layout root ships `min-width: fit-content`,
                        // which lets wide intrinsic content stretch the shell past
                        // the viewport. Wide content must scroll inside its own
                        // container, never the page body.
                        minWidth: 0,
                    },

                    // --- LÓGICA SOLO PARA ESCRITORIO ---
                    // Fija la altura del layout a la de la pantalla y prohíbe el scroll global.
                    ...(!isMobile && {
                        height: '100vh',
                        overflow: 'hidden',
                    }),

                    // 2. El marco principal que contiene todo. Ancla
                    //    (position:relative) de la AppBar de escritorio, que va
                    //    absoluta sobre la columna de contenido (ver CustomAppBar).
                    '& .RaLayout-appFrame': {
                        marginTop: 0,
                        alignItems: 'flex-start',
                        ...(!isMobile && {
                            height: '100%',
                            position: 'relative',
                        }),
                    },

                    // Patrón A · gotcha react-admin: el sidebar se monta como Drawer
                    // docked → .RaSidebar-docked > .RaSidebar-paper > .RaSidebar-fixed.
                    // El paper trae su propio overflow/altura que recorta el rail por
                    // abajo: forzar la cadena a 100vh + overflow visible para que el
                    // rail llegue de arriba abajo, a ras de los bordes.
                    '& .RaSidebar-docked': {
                        margin: '0 !important',
                        padding: '0 !important',
                        ...(!isMobile && { height: '100vh' }),
                    },
                    '& .RaSidebar-paper': {
                        border: 'none',
                        backgroundColor: 'transparent',
                        margin: '0 !important',
                        padding: '0 !important',
                        ...(!isMobile && {
                            position: 'relative',
                            height: '100vh',
                            overflow: 'visible',
                        }),
                    },
                    '& .RaSidebar-fixed': {
                        margin: '0 !important',
                        ...(!isMobile && {
                            position: 'relative !important',
                            height: '100vh !important',
                            overflow: 'visible !important',
                        }),
                    },

                    // 3. Contenedor que agrupa Menú y Contenido. Patrón A: sin
                    //    padding externo — el rail queda a ras de los bordes y la
                    //    fila ocupa toda la altura del viewport (la AppBar es
                    //    absoluta, así que esta fila empieza en y=0).
                    '& .RaLayout-contentWithSidebar': {
                        display: 'flex',
                        flex: 1,
                        gap: 0,
                        width: '100%',
                        ...(!isMobile && {
                            padding: 0,
                            height: '100vh',
                            overflow: 'hidden',
                        }),
                    },

                    '& .RaLayout-content': {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,

                        // --- LÓGICA SOLO PARA MÓVIL ---
                        ...(isMobile && {
                            height: 'calc(100dvh - 52px)',
                            paddingTop: '52px',
                            paddingBottom: 0,
                            overflow: 'hidden',
                        }),

                        // --- LÓGICA SOLO PARA ESCRITORIO ---
                        // Reserva la franja de la AppBar absoluta (44px) y aplica el
                        // padding de página estándar (24px) en lados/abajo.
                        ...(!isMobile && {
                            height: '100vh',
                            paddingTop: isHMISimulator ? '44px' : '68px',
                            paddingLeft: isHMISimulator ? 0 : theme.spacing(3),
                            paddingRight: isHMISimulator ? 0 : theme.spacing(3),
                            paddingBottom: isHMISimulator ? 0 : theme.spacing(3),
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }),
                    },

                    // 4. Lienzo de contenido — mismo fondo cálido que el canvas;
                    //    cada sección (header, lista, tarjetas) flota como tarjeta
                    //    propia encima (v3: no hay tarjeta envolvente de página).
                    '& .RaLayout-content > div': {
                        width: '100%',
                        backgroundColor: isHMISimulator ? 'transparent' : theme.palette.background.default,
                        WebkitOverflowScrolling: 'touch',
                        borderRadius: 0,
                        boxShadow: 'none',
                        ...(!isMobile && {
                            overflow: isHMISimulator ? 'hidden' : 'auto',
                            height: '100%',
                            // Column so a list page's stack can let the List fill
                            // the remaining height and scroll the table internally.
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }),
                        ...(isMobile && {
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            maxWidth: '100vw',
                        }),
                    },
                }}
            >
                {props.children}
            </Layout>

            {/* Diálogo de expiración de token */}
            {showExpiryDialog && (
                <TokenExpiryDialog
                open={showExpiryDialog}
                secondsRemaining={secondsRemaining}
                onExtendSession={handleExtendSession}
                onLogout={logout}
            />
            )}
        </>
    );
};

export default CustomLayout;